"use client";

/**
 * useNotifications Hook
 * S89: In-app notifications
 * S97: Filter tabs + load-more pagination
 *
 * Uses realtime for new notifications, with a visibility refresh to catch
 * events missed while the tab was hidden.
 * Provides mark-as-read, mark-all-read, filter, and pagination actions.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createClient } from "@/lib/supabase/client";
import { toast } from "sonner";

const PAGE_SIZE = 20;

export interface Notification {
  id: string;
  event_type: string;
  channel: string;
  title: string;
  body: string;
  link_to: string | null;
  document_id: string | null;
  read_at: string | null;
  sent_at: string | null;
  created_at: string;
  metadata: Record<string, unknown> | null;
}

interface NotificationsResponse {
  notifications: Notification[];
  unread_count: number;
  has_more: boolean;
}

export interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  hasMore: boolean;
  unreadOnly: boolean;
  setUnreadOnly: (v: boolean) => void;
  fetchMore: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [unreadOnly, setUnreadOnlyState] = useState(false);
  const offsetRef = useRef(0);
  const isMountedRef = useRef(true);
  const countRequestRef = useRef<AbortController | null>(null);

  const supabase = useMemo(() => createClient(), []);

  const fetchNotifications = useCallback(
    async (mode: "full" | "count" | "append") => {
      try {
        if (mode === "count") {
          countRequestRef.current?.abort();
          const controller = new AbortController();
          countRequestRef.current = controller;

          const res = await fetch("/api/notifications?limit=0", {
            signal: controller.signal,
          });
          if (!res.ok || !isMountedRef.current) return;
          const data: NotificationsResponse = await res.json();
          setUnreadCount(data.unread_count);
          return;
        }

        const offset = mode === "append" ? offsetRef.current : 0;
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          offset: String(offset),
        });
        if (unreadOnly) params.set("unread_only", "true");

        const res = await fetch(`/api/notifications?${params}`);
        if (!res.ok || !isMountedRef.current) return;

        const data: NotificationsResponse = await res.json();
        setUnreadCount(data.unread_count);
        setHasMore(data.has_more);

        if (mode === "append") {
          setNotifications((prev) => {
            const existingIds = new Set(prev.map((n) => n.id));
            const newItems = data.notifications.filter(
              (n) => !existingIds.has(n.id)
            );
            return [...prev, ...newItems];
          });
        } else {
          setNotifications(data.notifications);
          offsetRef.current = 0;
        }
      } catch {
        // Silently fail — network errors during polling are expected
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [unreadOnly]
  );

  const refresh = useCallback(async () => {
    offsetRef.current = 0;
    await fetchNotifications("full");
  }, [fetchNotifications]);

  const fetchMore = useCallback(async () => {
    offsetRef.current += PAGE_SIZE;
    await fetchNotifications("append");
  }, [fetchNotifications]);

  const setUnreadOnly = useCallback(
    (v: boolean) => {
      setUnreadOnlyState(v);
      // The useEffect watching unreadOnly will re-fetch
    },
    []
  );

  const markAsRead = useCallback(
    async (id: string) => {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, read_at: new Date().toISOString() } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      try {
        const res = await fetch(`/api/notifications/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ read: true }),
        });
        if (!res.ok) {
          await fetchNotifications("full");
        }
      } catch {
        await fetchNotifications("full");
      }
    },
    [fetchNotifications]
  );

  const markAllAsRead = useCallback(async () => {
    setNotifications((prev) =>
      prev.map((n) => ({
        ...n,
        read_at: n.read_at || new Date().toISOString(),
      }))
    );
    setUnreadCount(0);

    try {
      const res = await fetch("/api/notifications/mark-all-read", {
        method: "POST",
      });
      if (!res.ok) {
        await fetchNotifications("full");
      }
    } catch {
      await fetchNotifications("full");
    }
  }, [fetchNotifications]);

  // Initial fetch is count-only for fast page load. Realtime delivers new
  // notifications; visibility refresh catches anything missed while hidden.
  useEffect(() => {
    isMountedRef.current = true;
    fetchNotifications("count");

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchNotifications("count");
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      isMountedRef.current = false;
      countRequestRef.current?.abort();
      countRequestRef.current = null;
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchNotifications]);

  // Keep a stable ref so the realtime callback can call the latest version
  // without adding fetchNotifications to the effect deps (which would
  // tear down / recreate the channel on every unreadOnly toggle).
  const fetchRef = useRef(fetchNotifications);
  useEffect(() => { fetchRef.current = fetchNotifications; }, [fetchNotifications]);

  // Realtime subscription — instant delivery for new notifications
  useEffect(() => {
    let lastRefreshAttempt = 0;

    const channel = supabase
      .channel("notifications-realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications" },
        (payload) => {
          const row = payload.new as Notification;
          // Skip if filtering to unread only and this is already read (unlikely for INSERT but safe)
          setNotifications((prev) => {
            if (prev.some((n) => n.id === row.id)) return prev;
            return [row, ...prev];
          });
          setUnreadCount((prev) => prev + 1);
          const rawErpUrl = (row.metadata as Record<string, unknown> | null)?.erpUrl;
          const erpUrl = typeof rawErpUrl === "string" && /^https?:\/\//.test(rawErpUrl) ? rawErpUrl : undefined;
          toast(row.title, {
            description: row.body,
            ...(erpUrl && {
              action: {
                label: "View in ERP",
                onClick: () => window.open(erpUrl, "_blank", "noopener,noreferrer"),
              },
            }),
          });
        }
      )
      .subscribe((status, err) => {
        if (err) {
          if (err?.message?.includes("InvalidJWTToken") || err?.message?.includes("Token has expired")) {
            const now = Date.now();
            if (now - lastRefreshAttempt > 30_000) {
              lastRefreshAttempt = now;
              void supabase.auth.refreshSession();
            }
            return;
          }
          const isTransient = err?.message?.includes("1006");
          if (!isTransient) {
            console.warn("Notifications realtime error:", err);
          }
        }
        // Re-fetch on reconnect to catch notifications received during the gap
        if (status === "SUBSCRIBED") {
          fetchRef.current("full");
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabase]);

  return {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    unreadOnly,
    setUnreadOnly,
    fetchMore,
    markAsRead,
    markAllAsRead,
    refresh,
  };
}
